import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Pause, Activity, TrendingUp, Target, AlertCircle, LineChart } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Type2BotPerformance } from '@/services/conditional-skip-stats-service';
import { BotPatternPerformance } from './BotPatternPerformance';
import { PatternComboSuggestions } from './PatternComboSuggestions';
import { WinRateTimelineChart } from './WinRateTimelineChart';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';

interface Type2BotDetailDialogProps {
  bot: Type2BotPerformance | null;
  timeRange: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh: () => void;
}

export const Type2BotDetailDialog: React.FC<Type2BotDetailDialogProps> = ({
  bot,
  timeRange,
  open,
  onOpenChange,
  onRefresh
}) => {
  const [isPausing, setIsPausing] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('overview');

  // Fetch setting_id for this bot
  const { data: settingData } = useQuery({
    queryKey: ['bot-setting-id', bot?.bot_id],
    queryFn: async () => {
      if (!bot?.bot_id) return null;
      const { data, error } = await supabase
        .from('conditional_skip_settings')
        .select('id')
        .eq('bot_id', bot.bot_id)
        .eq('skip_type', 'FLEXIBLE_PATTERN')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!bot?.bot_id
  });

  const handlePauseBot = async () => {
    if (!bot) return;
    setIsPausing(true);
    try {
      const { error } = await supabase
        .from('bot_accounts')
        .update({ status: 'paused' })
        .eq('id', bot.bot_id);

      if (error) throw error;

      toast.success('Bot đã được tạm dừng');
      onRefresh();
      onOpenChange(false);
    } catch (error) {
      console.error('Error pausing bot:', error);
      toast.error('Không thể tạm dừng bot');
    } finally {
      setIsPausing(false);
    }
  };

  const getWinRateColor = (winRate: number, totalTrades: number) => {
    if (totalTrades === 0) return 'text-muted-foreground';
    if (winRate >= 60) return 'text-green-500 font-semibold';
    if (winRate >= 50) return 'text-orange-400';
    return 'text-red-500 font-semibold';
  };

  if (!bot) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-[85vw] lg:max-w-[80vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {bot.bot_name}
            <Badge variant={bot.is_active ? "default" : "secondary"}>
              {bot.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Chi tiết hiệu suất và cấu hình pattern rules
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="timeline">
              <LineChart className="h-4 w-4 mr-2" />
              Timeline
            </TabsTrigger>
            <TabsTrigger value="patterns">Patterns</TabsTrigger>
            <TabsTrigger value="suggestions">Suggestions</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Win Rate (All)</p>
                      <p className="text-2xl font-bold text-muted-foreground">
                        {bot.all_signals_trades > 0 
                          ? `${bot.all_signals_win_rate.toFixed(1)}%`
                          : '-'
                        }
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {bot.all_signals_wins}W / {bot.all_signals_losses}L
                      </p>
                    </div>
                    <Target className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Win Rate (Filtered)</p>
                      <p className={`text-2xl font-bold ${getWinRateColor(bot.win_rate, bot.trades)}`}>
                        {bot.trades > 0 ? `${bot.win_rate.toFixed(1)}%` : '-'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {bot.wins}W / {bot.losses}L
                      </p>
                    </div>
                    <Target className="h-8 w-8 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Improvement</p>
                      <p className={`text-2xl font-bold ${
                        bot.win_rate_improvement_pts > 5 ? 'text-green-600' :
                        bot.win_rate_improvement_pts < -5 ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {bot.win_rate_improvement_pts > 0 ? '+' : ''}
                        {bot.win_rate_improvement_pts.toFixed(1)} pts
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {bot.win_rate_improvement_pts > 5 ? '↑ Hiệu quả cao' : 
                         bot.win_rate_improvement_pts < -5 ? '↓ Cần cải thiện' : 
                         '~ Trung bình'}
                      </p>
                    </div>
                    <TrendingUp className={`h-8 w-8 ${
                      bot.win_rate_improvement_pts > 5 ? 'text-green-600' :
                      bot.win_rate_improvement_pts < -5 ? 'text-red-600' :
                      'text-yellow-600'
                    }`} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Forward Rate</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {bot.forward_rate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {bot.trades} / {bot.total_signals} signals
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Signals</p>
                    <p className="text-2xl font-bold">{bot.total_signals}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {bot.trades} forwarded
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Last Signal</p>
                    <p className="text-sm font-semibold mt-2">
                      {bot.last_signal_at 
                        ? formatDistanceToNow(new Date(bot.last_signal_at), { addSuffix: true })
                        : 'Never'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex gap-2 pt-4">
              {bot.is_active && bot.win_rate < 40 && bot.trades >= 10 && (
                <Button
                  variant="destructive"
                  onClick={handlePauseBot}
                  disabled={isPausing}
                >
                  <Pause className="h-4 w-4 mr-2" />
                  {isPausing ? 'Đang tạm dừng...' : 'Tạm dừng Bot'}
                </Button>
              )}
              {bot.win_rate < 40 && bot.trades >= 10 && (
                <div className="flex items-center gap-2 text-sm text-orange-600">
                  <AlertCircle className="h-4 w-4" />
                  Bot có win rate thấp. Cân nhắc tạm dừng hoặc điều chỉnh pattern rules.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Win Rate Timeline Tab */}
          <TabsContent value="timeline" className="space-y-4">
            <WinRateTimelineChart 
              botId={bot.bot_id}
              initialTimeRange={timeRange as any}
            />
          </TabsContent>

          {/* Pattern Performance Tab */}
          <TabsContent value="patterns" className="space-y-4">
            {settingData?.id ? (
              <BotPatternPerformance
                settingId={settingData.id}
                botId={bot.bot_id}
                timeRange={timeRange}
              />
            ) : (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <Activity className="h-16 w-16 mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">Chưa có cấu hình Type 2</h3>
                  <p className="text-sm text-muted-foreground mb-4 max-w-md">
                    Bot này chưa có cấu hình pattern rules. Bạn có thể tạo pattern rules mới từ tab "Pattern Suggestions".
                  </p>
                  <Button 
                    onClick={() => setActiveTab('suggestions')}
                    variant="default"
                  >
                    Đi đến Pattern Suggestions →
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Pattern Suggestions Tab */}
          <TabsContent value="suggestions" className="space-y-4">
            <PatternComboSuggestions
              focusedBotId={bot.bot_id}
              focusedSettingId={settingData?.id}
              focusedBotName={bot.bot_name}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
